import { FormattedText, ImageComponent, SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import React from "react";
import type { TextNodeType } from "../../../../components/FormattedText/FormattedText";
import type { TextButtonProps } from "../../types";
import {
  LeftIconWrapper,
  RightIconWrapper,
  TextButtonContainer,
  TextButtonContent,
  TextButtonIcon,
  TextButtonStyleProps,
} from "./TextButton.style";

/**
 * TextButton - Atomic level reusable text button component
 *
 * A text-based button component with optional left and right icons.
 * Designed for use cases like "View all benefits" with arrow indicators.
 * If no right icon is provided, defaults to blueChev icon.
 *
 * For tooltip functionality, wrap this component with OrderDetailV2Tooltip.
 *
 * @example
 * ```tsx
 * // With default icon (blueChev)
 * <TextButton
 *   btnTxt="View all 15 benefits"
 *   onClick={() => handleViewBenefits()}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With tooltip (composed separately)
 * <OrderDetailV2Tooltip
 *   message="Please wait till policy document is generated"
 *   trigger="hover"
 *   position="bottom"
 * >
 *   <TextButton
 *     btnTxt="Edit Details"
 *     disabled
 *     onClick={() => handleEdit()}
 *   />
 * </OrderDetailV2Tooltip>
 * ```
 *
 * @example
 * ```tsx
 * // With left and right icons
 * <TextButton
 *   btnTxt="View Details"
 *   leftIcon="/path/to/left-icon.svg"
 *   rightIcon="/path/to/right-icon.svg"
 *   onClick={() => handleClick()}
 * />
 * ```
 */
interface LeftIconProps {
  iconSrc?: string;
  disabled?: boolean;
}

const LeftIcon: React.FC<LeftIconProps> = ({ iconSrc, disabled }) => {
  if (!iconSrc) {
    return null;
  }

  return (
    <TextButtonIcon disabled={disabled}>
      <ImageComponent src={iconSrc} width="16px" height="16px" />
    </TextButtonIcon>
  );
};

interface RightIconProps {
  rightIcon?: string;
  showDefaultRightIcon?: boolean;
  disabled?: boolean;
}

const RightIcon: React.FC<RightIconProps> = ({
  rightIcon,
  showDefaultRightIcon = true,
  disabled,
}) => {
  // If showDefaultRightIcon is false and rightIcon is provided, use rightIcon
  if (!showDefaultRightIcon && rightIcon) {
    return (
      <TextButtonIcon disabled={disabled}>
        <ImageComponent src={rightIcon} width="12px" height="12px" />
      </TextButtonIcon>
    );
  }
  // If showDefaultRightIcon is false and no rightIcon, show nothing
  if (!showDefaultRightIcon && !rightIcon) {
    return null;
  }
  // Default: show blueChev icon (or rightIcon if provided and showDefaultRightIcon is true)
  const iconSrc =
    rightIcon ||
    "https://tripmoneycmsimgak.mmtcdn.com/img/blue_Chev_2800e4f975.svg";
  return (
    <TextButtonIcon disabled={disabled}>
      <ImageComponent src={iconSrc} width="12px" height="12px" />
    </TextButtonIcon>
  );
};

interface ButtonTextProps {
  btnTxt?: string | TextNodeType;
  disabled?: boolean;
}

const ButtonText: React.FC<ButtonTextProps> = ({ btnTxt, disabled }) => {
  if (!btnTxt) {
    return null;
  }

  if (typeof btnTxt === "string") {
    return (
      <SpanTag
        fontSize="sm"
        fontWeight="bold"
        color={disabled ? theme.greyText : theme.blueText}
      >
        {btnTxt}
      </SpanTag>
    );
  }

  return (
    <SpanTag
      fontSize="sm"
      fontWeight="bold"
      color={disabled ? theme.greyText : theme.blueText}
    >
      <FormattedText value={btnTxt} />
    </SpanTag>
  );
};

const TextButton: React.FC<TextButtonProps & TextButtonStyleProps> = ({
  btnTxt,
  leftIcon,
  rightIcon,
  showDefaultRightIcon = true,
  onClick,
  fullWidth = false,
  hasBorder = true,
  disabled = false,
  className,
  type = "button",
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <TextButtonContainer
      onClick={handleClick}
      fullWidth={fullWidth}
      hasBorder={hasBorder}
      disabled={disabled}
      className={className}
      type={type}
      aria-disabled={disabled}
      aria-label={typeof btnTxt === "string" ? btnTxt : undefined}
    >
      <TextButtonContent>
        <LeftIconWrapper>
          <LeftIcon iconSrc={leftIcon} disabled={disabled} />
        </LeftIconWrapper>
        <ButtonText btnTxt={btnTxt} disabled={disabled} />
        <RightIconWrapper>
          <RightIcon
            rightIcon={rightIcon}
            showDefaultRightIcon={showDefaultRightIcon}
            disabled={disabled}
          />
        </RightIconWrapper>
      </TextButtonContent>
    </TextButtonContainer>
  );
};

export default TextButton;
